import { memo } from 'react';
import { Calendar, MapPin, Phone, Mail } from 'lucide-react';
import { getRequestStatusBadgeClasses, getRequestStatusLabel, DateUtils, PhoneUtils } from '@cleanpro/common-ui';
import type { RequestWithRelations } from '../../../../../apps/admin/src/features/requests/types';

interface RequestRowProps {
  request: RequestWithRelations;
  onClick: (request: RequestWithRelations) => void;
}


const RequestRow = memo(({ request, onClick }: RequestRowProps) => {
  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onClick(request)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm text-gray-900">
            {request.service?.name ?? 'Unknown Service'}
          </div>
          {request.estimatedDuration && (
            <div className="text-xs text-gray-400 mt-1">
              Est. {DateUtils.formatDurationByMinutes(request.estimatedDuration)}
            </div>
          )}
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          <div className="flex items-start">
            <MapPin className="h-3 w-3 mr-1 text-gray-400 mt-0.5 flex-shrink-0" />
            <div className="max-w-xs">
              {(() => {
                const addressParts = [
                  request.property?.addressLine1,
                  request.property?.addressLine2,
                  request.property?.city,
                  request.property?.state,
                  request.property?.postalCode
                ].filter(Boolean); // Remove undefined/null values
                
                if (addressParts.length === 0) {
                  return <span className="text-gray-400 italic">No address</span>;
                }
                
                // Format as: "Street Address, City, State Postal"
                const formattedAddress = addressParts.join(', ');
                
                return <div className="truncate">{formattedAddress}</div>;
              })()}
            </div>
          </div>
          {request.accessInstructions && (
            <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
              Access: {request.accessInstructions}
            </div>
          )}
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {request.customer ? request.customer.name : <span className="text-gray-400 italic">No customer</span>}
        </div>
        <div className="text-sm text-gray-900">
          {request.customer?.email ? (
            <div className="flex items-center">
              <Mail className="h-3 w-3 mr-1 text-gray-400" />
              <span className="truncate max-w-xs">{request.customer.email}</span>
            </div>
          ) : (
            <span className="text-gray-400 italic">No email</span>
          )}
          {request.customer?.phone && (
            <div className="flex items-center mt-1">
              <Phone className="h-3 w-3 mr-1 text-gray-400" />
              <span>{PhoneUtils.format(request.customer.phone)}</span>
            </div>
          )}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-lg ${getRequestStatusBadgeClasses(request.status)}`}>
          {getRequestStatusLabel(request.status)}
        </span>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {request.requestedDateTime && (
            <div className="text-sm text-gray-900 flex items-start">
              <Calendar className="h-3 w-3 mr-1 text-gray-400 mt-0.5" />
              <span>{DateUtils.formatDateTimeWithRelative(request.requestedDateTime)}</span>
            </div>
          )}
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 flex items-start">
          <Calendar className="h-3 w-3 mr-1 text-gray-400 mt-0.5" />
          <span>{DateUtils.formatDateTimeWithRelative(request.createdAt)}</span>
        </div>
      </td>
      
    </tr>
  );
});

RequestRow.displayName = 'RequestRow';

export default RequestRow;